import React, { useState, useMemo } from 'react';
import { AlternativeRankService } from '../../../services/AlternativeRankService';

interface AltRanksTabProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

export const AltRanksTab: React.FC<AltRanksTabProps> = ({ searchTerm, setSearchTerm }) => {
  const allRanks = useMemo(() => AlternativeRankService.getAllAlternativeRanks(), []);
  const [selectedCareer, setSelectedCareer] = useState<string>('all');
  const [selectedRulebook, setSelectedRulebook] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const careers = useMemo(() => [...new Set(allRanks.map((r) => r.career))].sort(), [allRanks]);
  const rulebooks = useMemo(() => [...new Set(allRanks.map((r) => r.rulebook))].sort(), [allRanks]);

  const filtered = useMemo(() => {
    let ranks = allRanks;
    if (selectedCareer !== 'all') ranks = ranks.filter((r) => r.career === selectedCareer);
    if (selectedRulebook !== 'all') ranks = ranks.filter((r) => r.rulebook === selectedRulebook);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      ranks = ranks.filter(
        (r) =>
          r.name.toLowerCase().includes(term) ||
          r.career.toLowerCase().includes(term) ||
          r.description.toLowerCase().includes(term) ||
          r.prerequisites.some((p) => p.name.toLowerCase().includes(term)) ||
          r.advancements.some((a) => a.name.toLowerCase().includes(term))
      );
    }
    return ranks;
  }, [allRanks, selectedCareer, selectedRulebook, searchTerm]);

  return (
    <div>
      {/* Filters */}
      <div className="bg-surface-alt rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Search alt-ranks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-2 border border-line rounded-lg bg-surface text-ink"
          />
          <select
            value={selectedCareer}
            onChange={(e) => setSelectedCareer(e.target.value)}
            className="px-3 py-2 border border-line rounded-lg bg-surface text-ink"
          >
            <option value="all">All Careers ({allRanks.length})</option>
            {careers.map((c) => (
              <option key={c} value={c}>
                {c} ({allRanks.filter((r) => r.career === c).length})
              </option>
            ))}
          </select>
          <select
            value={selectedRulebook}
            onChange={(e) => setSelectedRulebook(e.target.value)}
            className="px-3 py-2 border border-line rounded-lg bg-surface text-ink"
          >
            <option value="all">All Rulebooks</option>
            {rulebooks.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results count */}
      <p className="text-sm text-ink-faint mb-4">
        {filtered.length} alt-rank{filtered.length !== 1 ? 's' : ''}
      </p>

      {/* Cards */}
      <div className="space-y-3">
        {filtered.map((rank) => (
          <div key={rank.id} className="border border-line rounded-lg overflow-hidden bg-surface">
            <button
              onClick={() => setExpandedId(expandedId === rank.id ? null : rank.id)}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-surface-hover transition-colors"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-ink">{rank.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                    {rank.career}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200">
                    Rank {rank.minimumRank}+
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-surface-hover text-ink-dim">
                    📖 {rank.rulebook}
                  </span>
                </div>
                {rank.prerequisites.length > 0 && (
                  <div className="text-xs text-ink-faint mt-1">
                    Prerequisites:{' '}
                    {rank.prerequisites
                      .map((p) => (p.value ? `${p.name} ${p.value}` : p.name))
                      .join(', ')}
                  </div>
                )}
              </div>
              <span className="text-ink-faint ml-2">{expandedId === rank.id ? '▼' : '▶'}</span>
            </button>

            {expandedId === rank.id && (
              <div className="px-4 py-3 border-t border-line bg-surface-alt">
                {/* Description */}
                {rank.description && (
                  <p className="text-sm text-ink-dim mb-3">{rank.description}</p>
                )}

                {/* Granted abilities */}
                {(rank.grantedTalents?.length || rank.grantedTraits?.length) && (
                  <div className="mb-3">
                    <h4 className="text-xs font-bold uppercase text-ink-faint mb-1">
                      Granted on Entry
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {rank.grantedTalents?.map((t) => (
                        <span
                          key={t}
                          className="text-xs px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200"
                        >
                          {t}
                        </span>
                      ))}
                      {rank.grantedTraits?.map((t) => (
                        <span
                          key={t}
                          className="text-xs px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Advancement table */}
                <h4 className="text-xs font-bold uppercase text-ink-faint mb-2">
                  Advancements ({rank.advancements.length})
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-ink-faint border-b border-line">
                        <th className="pb-1 pr-4">Name</th>
                        <th className="pb-1 pr-4">Type</th>
                        <th className="pb-1 pr-4">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rank.advancements.map((adv) => (
                        <tr key={adv.id} className="border-b border-line">
                          <td className="py-1 pr-4 text-ink">{adv.name}</td>
                          <td className="py-1 pr-4">
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded ${
                                adv.type === 'Skill'
                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                  : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                              }`}
                            >
                              {adv.type}
                            </span>
                          </td>
                          <td className="py-1 pr-4 text-ink-dim">{adv.cost}xp</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
